<template>
        <v-card class="rounded-xl elevation-3 mb-4 overflow-hidden">
            <v-card-title class="text-h6 font-weight-bold pt-4 pb-3 px-6 grey lighten-5 border-b">
                <v-icon left color="teal darken-4" class="mr-2">mdi-briefcase-outline</v-icon>
                Phân Quyền Quản Lý - Văn Phòng
                <v-spacer></v-spacer> 
                <v-chip color="teal lighten-4" text-color="teal darken-4" class="font-weight-bold shadow-sm" small>
                    <v-icon left small>mdi-information-outline</v-icon>
                    Sơ đồ phân cấp 1-Nhiều (Phòng -> Bộ Phận -> Team -> Tổ)
                </v-chip>
            </v-card-title>
            
            <v-row no-gutters style="min-height: calc(100vh - 200px);">
                <!-- CỘT TRÁI: CÂY CƠ CẤU -->
                <v-col cols="12" md="6" lg="5" class="bg-grey-lighten-4 border-r d-flex flex-column">
                    <div class="pa-4 border-b bg-white d-flex align-center">
                        <v-text-field
                            v-model="search"
                            label="Tìm kiếm trong sơ đồ..."
                            prepend-inner-icon="mdi-magnify"
                            dense
                            outlined
                            hide-details
                            clearable
                            class="bg-white rounded-lg shadow-sm"
                        ></v-text-field>
                        <v-btn icon color="blue-grey" class="ml-2 bg-grey-lighten-3" @click="fetchData">
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </div>

                    <div class="flex-grow-1 pa-4 overflow-y-auto" style="max-height: calc(100vh - 280px);">
                        <v-treeview
                            :items="orgTree"
                            :search="search"
                            activatable
                            item-key="code"
                            color="teal darken-3"
                            active-class="teal lighten-5 teal--text text--darken-4 font-weight-bold rounded-lg"
                            hoverable
                            @update:active="onNodeSelect"
                            :active="activeCodes"
                            class="custom-tree"
                        >
                            <template v-slot:prepend="{ item }">
                                <v-avatar size="28" :color="getLevelBgColor(item.level)" class="mr-1 shadow-sm">
                                    <v-icon small :color="getLevelColor(item.level)">{{ getLevelIcon(item.level) }}</v-icon>
                                </v-avatar>
                            </template>

                            <template v-slot:label="{ item }">
                                <div class="d-flex align-center py-2" style="width: 100%;">
                                    <div class="text-subtitle-2 mr-2 d-inline-block text-truncate" style="max-width: 200px;" :title="item.name">
                                        {{ item.name }}
                                    </div>
                                    <v-spacer></v-spacer>
                                    <!-- Hiển thị badge quản lý nếu có -->
                                    <v-chip v-if="item.managerName" x-small outlined :color="getLevelColor(item.level)" class="bg-white mr-2 flex-shrink-0" title="Quản lý phụ trách">
                                        <v-icon left x-small>mdi-account-tie</v-icon>
                                        {{ item.managerName.split('-')[0] }}
                                    </v-chip>
                                    
                                    <v-chip v-else x-small outlined color="red" class="bg-white mr-2 flex-shrink-0" title="Vacancy">
                                        <v-icon left x-small>mdi-alert-circle-outline</v-icon>
                                        Vacancy
                                    </v-chip>

                                    <!-- <v-tooltip bottom v-else>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon v-bind="attrs" v-on="on" small color="red lighten-2" class="mr-2" left >mdi-alert-circle-outline</v-icon>
                                        </template>
                                        <span>Thuộc cấp này chưa có quản lý</span>
                                    </v-tooltip> -->
                                </div>
                            </template>
                        </v-treeview>
                    </div>
                </v-col>

                <!-- CỘT PHẢI: CHI TIẾT VÀ GÁN QUẢN LÝ -->
                <v-col cols="12" md="6" lg="7" class="bg-white d-flex flex-column position-relative">
                    <div v-if="!activeNode" class="d-flex flex-column align-center justify-center flex-grow-1 grey--text text--lighten-1">
                        <v-icon size="80" color="grey lighten-3" class="mb-4">mdi-cursor-default-click</v-icon>
                        <h3 class="text-h6 font-weight-medium grey--text text--darken-1">Chọn một cấp cơ cấu bên trái</h3>
                        <p class="text-body-2">Để xem chi tiết và phân quyền người quản lý</p>
                    </div>

                    <div v-else class="flex-grow-1 fade-in">
                        <!-- Header Detail -->
                        <div class="pa-6 border-b d-flex align-center" :style="{ backgroundColor: getLevelBgColor(activeNode.level) }">
                            <v-avatar size="52" class="mr-4 bg-white shadow-sm">
                                <v-icon large :color="getLevelColor(activeNode.level)">{{ getLevelIcon(activeNode.level) }}</v-icon>
                            </v-avatar>
                            <div>
                                <div class="text-overline font-weight-bold mb-1" :style="{ color: getLevelColor(activeNode.level) }">
                                    {{ getLevelTitle(activeNode.level) }} {{ getParentPathText(activeNode) }}
                                </div>
                                <h2 class="text-h5 font-weight-bold blue-grey--text text--darken-4 mb-0">{{ activeNode.name }}</h2>
                            </div>
                        </div>

                        <!-- Form Card -->
                        <div class="pa-8">
                            <div class="d-flex align-center mb-6">
                                <v-icon color="indigo mr-2" left>mdi-account-star</v-icon>
                                <h3 class="text-h6 font-weight-bold indigo--text text--darken-4 mb-0">Người quản lý</h3>
                            </div>

                            <v-card outlined class="rounded-xl shadow-sm border-light mb-8 overflow-hidden">
                                <div class="d-flex bg-grey-lighten-4 border-b">
                                    <div class="pa-5 flex-grow-1">
                                        <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-1">
                                            Chức vụ đảm nhiệm
                                        </div>
                                        <v-chip label color="indigo lighten-5" text-color="indigo darken-4" class="font-weight-bold">
                                            {{ getManagerRoleTitle(activeNode.level) }}
                                        </v-chip>
                                    </div>
                                    <v-divider vertical></v-divider>
                                    <div class="pa-5 flex-grow-1">
                                        <!-- <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-1">
                                            Quyền hạn áp dụng
                                        </div>
                                        <div class="text-body-2">Quản lý toàn bộ nhân viên thuộc <b>{{ activeNode.name }}</b> {{ activeNode.children && activeNode.children.length ? 'và các nhánh con' : '' }}</div> -->
                                    </div>
                                </div>

                                <div class="pa-6 bg-white">
                                    <span class="d-block text-subtitle-2 font-weight-bold grey--text text--darken-3 mb-2">Chọn nhân sự phụ trách:</span>
                                    <v-autocomplete
                                        label="Nhập tên nhân viên cần phân quyền..."
                                        :items="users"
                                        item-text="name"
                                        item-value="code"
                                        v-model="form.managerId"
                                        :loading="loadingUsers"
                                        outlined
                                        clearable
                                        prepend-inner-icon="mdi-account-search"
                                        class="rounded-lg rounded-input"
                                        hide-details
                                    >
                                        <template v-slot:item="{ item }">
                                            <v-list-item-avatar color="indigo lighten-4" class="mr-3">
                                                <span class="indigo--text font-weight-bold">{{ (item.name || 'A').substring(0,1).toUpperCase() }}</span>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title class="font-weight-bold">{{ item.name }} ({{ item.code }})</v-list-item-title>
                                                <v-list-item-subtitle>Nhân viên</v-list-item-subtitle>
                                            </v-list-item-content>
                                        </template>
                                    </v-autocomplete>
                                </div>

                                <div class="px-6 pb-6 bg-white">
                                    <span class="d-block text-subtitle-2 font-weight-bold grey--text text--darken-3 mb-2">Ghi chú:</span>
                                    <v-textarea
                                        v-model="form.note"
                                        label="Nội dung ghi chú..."
                                        outlined
                                        dense
                                        rows="2"
                                        hide-details
                                        class="rounded-lg rounded-input"
                                        prepend-inner-icon="mdi-pencil"
                                    ></v-textarea>
                                </div>

                                <div class="px-6 pb-6 bg-white">
                                    <v-checkbox
                                        v-model="form.isImmediate"
                                        label="Áp dụng tức thời"
                                        color="teal darken-4"
                                        hide-details
                                        class="mb-2 mt-0 font-weight-bold"
                                    ></v-checkbox>
                                    
                                    <v-slide-y-transition>
                                        <div v-if="!form.isImmediate" class="mt-2">
                                            <span class="d-block text-subtitle-2 font-weight-bold grey--text text--darken-3 mb-2">Ngày áp dụng:</span>
                                            <v-text-field
                                                type="date"
                                                v-model="form.executeAt"
                                                :min="minExecuteDate"
                                                outlined
                                                dense
                                                hide-details
                                                class="rounded-lg rounded-input"
                                            ></v-text-field>
                                        </div>
                                    </v-slide-y-transition>
                                </div>
                            </v-card>

                            <div class="d-flex justify-end">
                                <v-btn 
                                    color="red darken-3" 
                                    text
                                    class="px-6 py-6 rounded-lg font-weight-bold mr-4"
                                    @click="hideDepartment"
                                    :loading="hiding"
                                >
                                    <v-icon left>mdi-eye-off</v-icon> Ẩn Cơ cấu này
                                </v-btn>
                                <v-btn 
                                    color="teal darken-4" 
                                    elevation="3" 
                                    class="white--text px-8 py-6 rounded-lg font-weight-bold text-subtitle-1 shadow-md"
                                    @click="saveManager"
                                    :loading="saving"
                                >
                                    <v-icon left>mdi-check-decagram</v-icon> Xác Nhận Cấu Hình
                                </v-btn>
                            </div>
                            
                            <v-alert v-if="saveSuccess" outlined type="success" text class="mt-4 rounded-lg font-weight-medium transition-swing">
                                Đã cập nhật người quản lý cho cấp <b>{{ activeNode.name }}</b> thành công!
                            </v-alert>

                            <!-- Bảng Kế hoạch phân quyền -->
                            <v-card outlined class="rounded-xl shadow-sm border-light mt-6 overflow-hidden">
                                <div class="px-6 py-4 bg-grey-lighten-4 border-b d-flex align-center">
                                    <v-icon color="orange darken-2" class="mr-2">mdi-calendar-clock</v-icon>
                                    <h4 class="text-subtitle-1 font-weight-bold blue-grey--text text--darken-3 mb-0">Kế hoạch phân quyền</h4>
                                </div>
                                <v-data-table
                                    :headers="planHeaders"
                                    :items="plans"
                                    :loading="loadingPlans"
                                    hide-default-footer
                                    class="elevation-0"
                                >
                                    <template v-slot:[`item.createdAt`]="{ item }">
                                        {{ formatDateTime(item.createdAt) }}
                                    </template>
                                    <template v-slot:[`item.execDate`]="{ item }">
                                        {{ formatDateTime(item.execDate) }}
                                    </template>
                                    <template v-slot:[`item.actions`]="{ item }">
                                        <v-btn icon color="red darken-2" @click="deletePlan(item.id)">
                                            <v-icon small>mdi-delete</v-icon>
                                        </v-btn>
                                    </template>
                                    <template slot="no-data">
                                        <div class="pa-4 grey--text text-center">Chưa có kế hoạch thay đổi nào</div>
                                    </template>
                                </v-data-table>
                            </v-card>
                        </div>
                    </div>
                </v-col>
            </v-row>
        </v-card>
</template>

<script>
import api from '../api';
import http from '@/components/services/http-common';
import moment from 'moment/moment';

export default {
    name: "OfficeRoleCard",
    data() {
        return {
            loading: false,
            saving: false,
            hiding: false,
            saveSuccess: false,
            search: "",
            activeCodes: [],
            activeNode: null,
            form: {
                managerId: null,
                note: null,
                isImmediate: true,
                executeAt: null
            },
            minExecuteDate: moment().add(1, 'days').format('YYYY-MM-DD'),
            users: [],
            loadingUsers: false,
            orgTree: [], // Dữ liệu dạng cây
            
            plans: [],
            loadingPlans: false,
            planHeaders: [
                { text: 'Mã nhân sự', value: 'employeeCode' },
                { text: 'Tên nhân sự', value: 'employeeName' },
                { text: 'Ngày thực thi', value: 'execDate' },
                { text: 'Ghi chú', value: 'note' },
                { text: 'Xóa', value: 'actions', sortable: false, align: 'center', width: '80px' }
            ],
        }
    },
    methods: {
        fetchData() {
            this.loading = true;
            api.getTree('office').then(data => {
                this.orgTree = data;
            }).catch(err => {
                console.error("fetch org tree error", err);
            }).finally(() => {
                this.loading = false;
            });
        },
        fetchEmployees() {
            this.loadingUsers = true;
            api.getEmployees({ dropDown: 1 }).then(data => {
                this.users = data;
            }).catch(err => {
                console.error("fetch employees error", err);
            }).finally(() => {
                this.loadingUsers = false;
            });
        },
        onNodeSelect(selection) {
            this.saveSuccess = false;
            this.plans = [];
            if (selection && selection.length > 0) {
                const code = selection[0];
                this.activeNode = this.findNode(code, this.orgTree);
                if (this.activeNode) {
                    this.fetchPlans(this.activeNode.code);
                    let matchedUser = null;
                    if (this.activeNode.managerCode) {
                        matchedUser = this.users.find(u => u.code === this.activeNode.managerCode);
                    } else if (this.activeNode.managerName) {
                        matchedUser = this.users.find(u => (u.name || '').includes(this.activeNode.managerName) || this.activeNode.managerName.includes(u.name));
                    }
                    this.form.managerId = matchedUser ? matchedUser.code : null;
                    this.form.note = this.activeNode.note || null;
                }
            } else {
                this.activeNode = null;
                this.form.managerId = null;
                this.form.note = null;
            }
        },
        findNode(code, list) {
            for (let i = 0; i < list.length; i++) {
                if (list[i].code === code) return list[i];
                if (list[i].children) {
                    let found = this.findNode(code, list[i].children);
                    if (found) return found;
                }
            }
            return null;
        },
        saveManager() {
            this.saving = true;
            this.saveSuccess = false;
            
            if (!this.activeNode) {
                this.saving = false;
                return;
            }

            const deptCode = this.activeNode.code;
            const manager = this.form.managerId;
            const level = this.activeNode.level;
            const note = this.form.note;
            const isImmediate = this.form.isImmediate;
            const executeAt = this.form.isImmediate ? null : (this.form.executeAt ? moment(this.form.executeAt).format("YYYY-MM-DD") : null);

            if (!this.form.isImmediate && !executeAt) {
                alert("Vui lòng chọn ngày áp dụng!");
                this.saving = false;
                return;
            }

            api.updateManager(deptCode, manager, level, note, executeAt, isImmediate).then(() => {
                this.saveSuccess = true;
                
                if (!executeAt) {
                    // Update local node state manually
                    this.activeNode.managerCode = manager;
                    if (manager) {
                        const matchedUser = this.users.find(u => u.code === manager);
                        this.activeNode.managerName = matchedUser ? (matchedUser.name || matchedUser.fullName) : manager;
                    } else {
                        this.activeNode.managerName = null;
                    }
                    this.activeNode.note = note;
                } else {
                    // Refresh plans table
                    this.fetchPlans(deptCode);
                }

                setTimeout(() => { this.saveSuccess = false; }, 3000);
            }).catch(err => {
                console.error("Save manager error:", err);
            }).finally(() => {
                this.saving = false;
            });
        },
        hideDepartment() {
            if (!this.activeNode) return;
            if (confirm(`Bạn có chắc chắn muốn ẩn phần tử "${this.activeNode.name}" không?`)) {
                this.hiding = true;
                http.delete(`departments/hide/${this.activeNode.code}`).then(() => {
                    this.activeNode = null;
                    this.fetchData(); // Reload tree
                }).catch(err => {
                    console.error("Hide department error:", err);
                    alert("Có lỗi xảy ra khi ẩn phòng ban");
                }).finally(() => {
                    this.hiding = false;
                });
            }
        },
        fetchPlans(deptCode) {
            this.loadingPlans = true;
            http.get(`departments/plan/${deptCode}`).then(res => {
                this.plans = res.data.data;
            }).catch(err => {
                console.error("fetch plans error:", err);
            }).finally(() => {
                this.loadingPlans = false;
            });
        },
        deletePlan(planId) {
            if (confirm("Bạn có chắc chắn muốn xóa kế hoạch này?")) {
                http.delete(`departments/plan/${planId}`).then(res => {
                    if (this.activeNode) this.fetchPlans(this.activeNode.code);
                }).catch(err => {
                    console.error("delete plan error:", err);
                });
            }
        },
        formatDateTime(val) {
            if (!val) return '';
            return moment(val).format("DD/MM/YYYY HH:mm");
        },
        getLevelIcon(level) {
            if (level === 0 || level === '0') return 'mdi-office-building';
            if (level === 1 || level === '1') return 'mdi-domain';
            if (level === 2 || level === '2') return 'mdi-account-group';
            return 'mdi-account-multiple';
        },
        getLevelColor(level) {
            if (level === 0 || level === '0') return '#1976D2'; // Blue
            if (level === 1 || level === '1') return '#00897B'; // Teal
            if (level === 2 || level === '2') return '#F57C00'; // Orange
            return '#8E24AA'; // Purple
        },
        getLevelBgColor(level) {
            if (level === 0 || level === '0') return '#E3F2FD';
            if (level === 1 || level === '1') return '#E0F2F1';
            if (level === 2 || level === '2') return '#FFF3E0';
            return '#F3E5F5';
        },
        getLevelTitle(level) {
            if (level === 0 || level === '0') return 'Cấp 1 • Phòng ban';
            if (level === 1 || level === '1') return 'Cấp 2 • Bộ phận';
            if (level === 2 || level === '2') return 'Cấp 3 • Team';
            return 'Cấp 4 • Tổ';
        },
        getManagerRoleTitle(level) {
            if (level === 0 || level === '0') return 'Giám đốc khối / Trưởng phòng';
            if (level === 1 || level === '1') return 'Trưởng bộ phận';
            if (level === 2 || level === '2') return 'Trưởng Team';
            return 'Trưởng Tổ';
        },
        getParentPathText(node) {
            if (node.level === 0 || node.level === '0') return '';
            return `(Trực thuộc cấp trên)`;
        }
    },
    created() {
        this.fetchData();
        this.fetchEmployees();
    }
}
</script>

<style scoped>
.shadow-sm { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
.shadow-md { box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
.border-b { border-bottom: 1px solid #edf2f7; }
.border-r { border-right: 1px solid #edf2f7; }
.border-light { border: 1px solid #cbd5e1; }

.bg-grey-lighten-4 { background-color: #f8fafc; }
.bg-grey-lighten-3 { background-color: #f1f5f9; }

/* Custom Treeview Spacing */
::v-deep .custom-tree .v-treeview-node__root {
    min-height: 44px;
    padding-top: 4px;
    padding-bottom: 4px;
    margin-bottom: 4px;
    transition: all 0.2s;
}
::v-deep .custom-tree .v-treeview-node__root:hover {
    background-color: #f1f5f9 !important;
}

.fade-in {
    animation: fadeIn 0.3s ease-in-out;
}
@keyframes fadeIn {
    from { opacity: 0; transform: translateY(5px); }
    to { opacity: 1; transform: translateY(0); }
}

::v-deep .rounded-input .v-input__control .v-input__slot {
    padding-top: 6px;
    padding-bottom: 6px;
}
</style>
